/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { commerceApiRequest, ContentEditableEvent, getUrlSync, Text } from '@msdyn365-commerce/core';
import { Address, BusinessPartnerProspect, CommerceProperty } from '@msdyn365-commerce/retail-proxy';
import { createProspectAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { toJS } from 'mobx';
import * as React from 'react';

import { INosBusinessSignUpConfig, INosBusinessSignUpProps, INosBusinessSignUpResources } from './nos-business-sign-up.props.autogenerated';
import { SignUpButton, SignUpError, SignUpInput, SignUpLabel } from './components/index';
import BusinessSignUpDropdown, { IBusinessSignUpDropdownOption } from './components/sign-up-dropdown';

import SectionHeader from './components/section-header';
import verifyEmailAction, { VerifyEmailInput } from '../../actions/verify-email.action';
import SignUpVAT from './components/sign-up-vat';
import SignUpEmailVerification from './components/sign-up-email-verification';
import validateEmailCodeAction, { ValidateEmailCodeInput } from '../../actions/validate-email-code.action';
import validateVatAction, { ValidateVatInput } from '../../actions/validate-vat.action';
import SignUpTermsConditions from './components/sign-up-terms-conditions';
import { IGetAvailableLanguagesData } from '../../actions/get-available-languages.action';
import { companySizes } from './components/sizeEnums';
import { GetAddressSuggestionInput, IGetAddressSuggestionData } from '../../actions/get-address-suggestion.action';
import debounce from 'lodash/debounce';
import {
    NOS_BUSINESS_SIGN_UP_AUTOCOMPLETE_CHAR_LIMIT,
    NOS_BUSINESS_SIGN_UP_AUTOCOMPLETE_DEBOUNCE_TIMEOUT,
    NOS_BUSINESS_SIGN_UP_CAMPAIGN_CODE_PARAM
} from '../../configuration/business-sign-up';

export interface IBusinessSignUpExtendedProps extends INosBusinessSignUpProps<INosBusinessSignUpConfig> {
    formItems?: IFormItem[];
}

export interface Suggestion {
    id: string;
    poi?: {
        name?: string;
    };
    address?: {
        postalCode?: string;
        localName?: string;
        country?: string;
        countryCodeISO3?: string;
        streetName?: string;
        streetNumber?: string;
        municipality?: string;
    };
}

export interface IBusinessSignUpItem {
    wrapper: INodeProps;
    isAddressInput?: boolean;
    isValidateVatButton?: boolean;
    isSectionHeader?: boolean;
    id: string;
    label: React.ReactNode;
    errorMessage: React.ReactNode;
    input?: React.ReactNode;
    url: string;
}

export interface IBusinessState {
    isReady: boolean;
    formItems: IFormItem[];
    addressHasErrors: boolean;
    showAddressErrors: boolean;
    hasSubmitErrors: boolean;
    isContinueWithoutVatId: boolean;
    isVatValidationClicked: boolean;
    isVatCheckExcecuted: boolean;
    isEmailVerificationInProcess: boolean;
    isEmailVerified: boolean;
    isUserVerificationFailed: boolean;
    emailValidationCode: string;
    isSubmittedPressed: boolean;
    isVatCheckFirstClickDone: boolean;
    isAcceptedTermsAndConditions: boolean;
    isUserExisting: boolean;
    hasExistingProspect: boolean;
    isVatIdValid: boolean;
    suggestions: [];
    selectedSuggestion: string;
    originOfInterestReferral: string;
}

export interface IBusinessSignUpLocalAccount {
    FormWrapper: INodeProps;
    items: IBusinessSignUpItem[];
    buttons: React.ReactNode[];
    errorMessage: React.ReactNode[];
    disclaimer: React.ReactNode;
}

export interface IBusinessSignUpViewProps {
    className: string;
    businessSignUp: IModuleProps;
    ContainerWrapper: INodeProps;
    heading: React.ReactNode;
    businessSignUpForm: IBusinessSignUpLocalAccount;
    formItems: IFormItem[];
}

export interface IFormItem {
    id: string;
    type: InputType;
    label: string;
    value: string;
    maxChars?: string;
    isRequired: boolean;
    pattern?: string;
    address?: Address;
    customClass?: string;
    isValid?: boolean;
    fieldReference?: React.RefObject<HTMLInputElement>;
    options?: IBusinessSignUpDropdownOption[];
    selectValue?: IBusinessSignUpDropdownOption | string;
    number?: string;
}

export enum InputType {
    // eslint-disable-next-line @typescript-eslint/no-shadow -- Text enum doesn't override the imported Text
    Text = 'text',
    Address = 'address',
    Email = 'email',
    TextArea = 'textarea',
    Button = 'button',
    Select = 'select',
    SectionHeader = 'sectionHeader',
    Checkbox = 'checkbox',
    Empty = 'empty',
    Phone = 'phone',
    VAT = 'vat',
    EmailVerification = 'emailVerification',
    Language = 'language',
    VATLabel = 'vatLabel',
    Country = 'Country',
    CompanyType = 'CompanyType',
    OriginOfInterest = 'OriginOfInterest'
}

export enum InputID {
    FirstName = 'FirstName',
    LastName = 'LastName',
    Email = 'CompanyEmail',
    EmailValidation = 'CompanyEmailValidation',
    EmailVerification = 'EmailVerification',
    CompanyName = 'CompanyName',
    Address = 'CompanyAddress',
    PhoneCode = 'PhoneCode',
    Phone = 'Phone',
    JobTitle = 'JobTitle',
    Size = 'CompanySize',
    Department = 'Department',
    Comment = 'Comment',
    VAT = 'VAT',
    ValidateVatButton = 'VatButton',
    Country = 'Country',
    Language = 'Language',
    Salutation = 'Salutation',
    CompanyType = 'CompanyType',
    AdditionalAddressInformation = 'AdditionallAddressInformation',
    ContinueWithoutVAT = 'ContinueWithoutVAT',
    ExistingCustomerNumber = 'ExistingCustomerNumber',
    OriginOfInterest = 'OriginOfInterest',
    PersonalSectionHeader = 'PersonalSectionHeader',
    EmailSectionHeader = 'EmailSectionHeader',
    CompanySectionHeader = 'CompanySectionHeader',
    VATIdSectionHeader = 'VATIdSectionHeader',
    Empty = 'Empty',
    Street = 'Street',
    HouseNumber = 'Number',
    ZipCode = 'ZipCode',
    City = 'City'
}

export interface AddressSuggestion {
    country: string;
    countryCode: string;
    countryCodeISO3: string;
    countrySecondarySubdivision: string;
    countrySubdivision: string;
    countrySubdivisionCode: string;
    countrySubdivisionName: string;
    freeformAddress: string;
    localName: string;
    municipality: string;
    neighbourhood: string;
    postalCode: string;
    streetName: string;
    streetNumber: string;
}

export const renderInputSection = (
    url: string,
    resources: INosBusinessSignUpResources,
    inputs: IFormItem[],
    className: string,
    needToReset: boolean,
    onInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void,
    showErrors: boolean,
    telemetryContent: ITelemetryContent,
    emailValidationCode: string,
    isSubmittedPressed: boolean,
    isUserExisting: boolean,
    hasExistingProspect: boolean,
    languageSlot?: React.ReactNode,
    countrySelect?: React.ReactNode,
    companyTypeSelect?: React.ReactNode,
    originOfInterestSelect?: React.ReactNode,
    phoneSlot?: React.ReactNode,
    updateAddress?: (errors: boolean, currAddress?: Address) => void,
    onButtonClick?: (inputId: string) => void,
    buttonClassName?: string,
    isContinueWithoutVatId?: boolean,
    isVatValidationClicked?: boolean,
    isVatCheckFirstClickDone?: boolean,
    onIsContinueWithoutVatIdChange?: () => void,
    isEmailVerificationInProcess?: boolean,
    isEmailVerified?: boolean,
    isUserVerificationFailed?: boolean,
    confirmCode?: (code: string) => void,
    updateLanguage?: (newChosenLanguage: IGetAvailableLanguagesData) => void,
    updateCountry?: (newChosenCountry: IGetAvailableLanguagesData) => void,
    updateCompanyType?: (newChosenCompanyType: IGetAvailableLanguagesData) => void,
    updateOriginOfInterest?: (newChosenOriginOfInterest: IGetAvailableLanguagesData) => void,
    updatePhone?: (newPhone: string) => void,
    updatePhoneCode?: (newPhoneCode: string) => void,
    isVatCheckExcecuted?: boolean,
    suggestions?: [],
    selectedSuggestion?: string,
    handleSuggestionSelect?: (suggestion: string) => void,
    updateCompanyAddress?: (inputType: string, newValue: string) => void
): IBusinessSignUpItem[] => {
    return inputs.map((element, index) => {
        const {
            id,
            type,
            label,
            maxChars,
            isRequired,
            pattern,
            customClass,
            value,
            fieldReference,
            options,
            selectValue,
            isValid,
            number
        } = element;

        const baseSignUpItem: IBusinessSignUpItem = {
            wrapper: {
                className: classnames(className, `${className}-${id}`, customClass)
            },
            id,
            url,
            label: <SignUpLabel {...{ id, forId: id, className, text: label }} />,
            errorMessage: <SignUpError {...{ className, type: 'item' }} />
        };

        if (type === InputType.Language) {
            if (languageSlot) {
                baseSignUpItem.isAddressInput = true;
                baseSignUpItem.input = (
                    <>
                        {React.cloneElement(languageSlot as React.ReactElement, {
                            updateDropdown: updateLanguage,
                            selectedValue: element.selectValue,
                            isSubmittedPressed
                        })}
                    </>
                );
            } else {
                // If the address slot is not filled, remove this input from the form
                inputs.splice(index, 1);
            }
        } else if (type === InputType.Country) {
            if (countrySelect) {
                baseSignUpItem.isAddressInput = true;
                baseSignUpItem.input = (
                    <>
                        {React.cloneElement(countrySelect as React.ReactElement, {
                            updateDropdown: updateCountry,
                            selectedValue: element.selectValue,
                            isSubmittedPressed
                        })}
                    </>
                );
            } else {
                // If the address slot is not filled, remove this input from the form
                inputs.splice(index, 1);
            }
        } else if (type === InputType.CompanyType) {
            if (companyTypeSelect) {
                baseSignUpItem.isAddressInput = true;
                baseSignUpItem.input = (
                    <>
                        {React.cloneElement(companyTypeSelect as React.ReactElement, {
                            updateDropdown: updateCompanyType,
                            selectedValue: element.selectValue,
                            isSubmittedPressed
                        })}
                    </>
                );
            } else {
                // If the address slot is not filled, remove this input from the form
                inputs.splice(index, 1);
            }
        } else if (type === InputType.OriginOfInterest) {
            if (originOfInterestSelect) {
                baseSignUpItem.isAddressInput = true;
                baseSignUpItem.input = (
                    <>
                        {React.cloneElement(originOfInterestSelect as React.ReactElement, {
                            updateDropdown: updateOriginOfInterest,
                            selectedValue: element.selectValue,
                            isSubmittedPressed
                        })}
                    </>
                );
            } else {
                // If the address slot is not filled, remove this input from the form
                inputs.splice(index, 1);
            }
        } else if (type === InputType.Phone) {
            if (phoneSlot) {
                baseSignUpItem.isAddressInput = true;

                baseSignUpItem.input = (
                    <>
                        {React.cloneElement(phoneSlot as React.ReactElement, {
                            updatePhone: updatePhone,
                            updatePhoneCode: updatePhoneCode,
                            selectedValue: element.selectValue,
                            inputValue: element.value,
                            className,
                            isSubmittedPressed
                        })}
                    </>
                );
            } else {
                // If the address slot is not filled, remove this input from the form
                inputs.splice(index, 1);
            }
        } else if (type === InputType.Empty) {
            baseSignUpItem.input = <React.Fragment></React.Fragment>;
        } else if (type === InputType.VAT) {
            if (onButtonClick && onIsContinueWithoutVatIdChange) {
                baseSignUpItem.input = (
                    <SignUpVAT
                        {...{
                            resources,
                            id,
                            type,
                            value,
                            className,
                            ariaLabel: 'label',
                            telemetryContent: telemetryContent,
                            text: value,
                            isRequired: isRequired === undefined ? false : isRequired,
                            buttonClassName,
                            onClick: () => onButtonClick(id),
                            isContinueWithoutVatId,
                            isVatCheckFirstClickDone,
                            isVatValidationClicked,
                            handleIscontinueWithoutVatId: () => onIsContinueWithoutVatIdChange(),
                            onChange: onInputChange,
                            isVatValid: isValid,
                            isVatCheckExcecuted,
                            isSubmittedPressed
                        }}
                    />
                );
            }
        } else if (type === InputType.EmailVerification) {
            if (onButtonClick && confirmCode) {
                baseSignUpItem.input = (
                    <SignUpEmailVerification
                        {...{
                            id,
                            type,
                            value,
                            className,
                            ariaLabel: 'label',
                            telemetryContent: telemetryContent,
                            text: value,
                            isRequired: isRequired === undefined ? false : isRequired,
                            buttonClassName,
                            onClick: () => onButtonClick(InputType.EmailVerification),
                            onConfirmCode: (code: string) => confirmCode(code),
                            onChange: onInputChange,
                            isEmailVerificationInProcess,
                            isEmailVerified,
                            isUserVerificationFailed,
                            emailValidationCode,
                            resources,
                            isSubmittedPressed,
                            isUserExisting,
                            hasExistingProspect,
                            url
                        }}
                    />
                );
            }
        } else if (type !== InputType.Button && type !== InputType.Select && type !== InputType.SectionHeader) {
            baseSignUpItem.input = (
                <SignUpInput
                    {...{
                        id,
                        type,
                        value,
                        maxLength: maxChars,
                        pattern,
                        className,
                        isRequired: isRequired === undefined ? false : isRequired,
                        onChange: onInputChange,
                        fieldReference,
                        isSubmittedPressed,
                        suggestions: suggestions,
                        selectedSuggestion: selectedSuggestion,
                        onSuggestionSelect: handleSuggestionSelect,
                        updateCompanyAddress: updateCompanyAddress
                    }}
                />
            );
        } else if (type === InputType.Button) {
            baseSignUpItem.isValidateVatButton = true;
            if (onButtonClick) {
                baseSignUpItem.input = (
                    // TODO finish button
                    <SignUpButton
                        {...{
                            id,
                            value,
                            className: buttonClassName ?? '',
                            ariaLabel: 'label',
                            telemetryContent: telemetryContent,
                            text: value,
                            onClick: () => onButtonClick(id)
                        }}
                    />
                );
            }
        } else if (type === InputType.Select) {
            if (options) {
                if (typeof selectValue !== 'string') {
                    baseSignUpItem.input = (
                        <BusinessSignUpDropdown
                            id={id}
                            value={value}
                            className={className}
                            isRequired={isRequired}
                            options={options}
                            onChange={(event: any) => onInputChange(event)}
                            selectValue={selectValue}
                            isSubmittedPressed={isSubmittedPressed}
                            resources={resources}
                        />
                    );
                }
            }
        } else if (type === InputType.SectionHeader) {
            baseSignUpItem.isSectionHeader = true;
            baseSignUpItem.input = <SectionHeader header={value} sectionLabel={label} number={number} />;
        }

        return baseSignUpItem;
    });
};

/**
 *
 * Business Request Form Modules
 * Form that a business can use to submit a request to become a partner with Microsoft.
 * @extends {React.Component<INosBusinessSignUpProps<INosBusinessSignUpConfig>>}
 */
class NosBusinessSignUp extends React.Component<IBusinessSignUpExtendedProps, IBusinessState> {
    // Used as default data
    public BusinessSignUpDefaultInputs: IFormItem[] = [
        {
            id: InputID.EmailSectionHeader,
            type: InputType.SectionHeader,
            label: this.props.resources.emailSectionHeaderLabel,
            value: this.props.resources.emailSectionHeader,
            isRequired: false,
            customClass: 'col-12',
            number: '1'
        },
        {
            id: InputID.EmailVerification,
            type: InputType.EmailVerification,
            label: this.props.resources.emailAddressLabel,
            value: '',
            isRequired: false,
            customClass: 'col-12'
        },
        {
            id: InputID.PersonalSectionHeader,
            type: InputType.SectionHeader,
            label: this.props.resources.personalSectionHeaderLabel,
            value: this.props.resources.personalSectionHeader,
            isRequired: false,
            customClass: 'col-12',
            number: '2'
        },
        {
            id: InputID.FirstName,
            type: InputType.Text,
            label: this.props.resources.firstNameLabel,
            value: '',
            isRequired: true,
            customClass: 'col-12 col-md-6',
            maxChars: '60'
        },
        {
            id: InputID.LastName,
            type: InputType.Text,
            label: this.props.resources.lastNameLabel,
            value: '',
            isRequired: true,
            customClass: 'col-12 col-md-6',
            maxChars: '60'
        },
        {
            id: InputID.Phone,
            type: InputType.Phone,
            label: this.props.resources.phoneLabelText,
            value: '',
            isRequired: true,
            customClass: 'col-12 col-md-6'
        },
        {
            id: InputID.Language,
            type: InputType.Language,
            value: '',
            label: this.props.resources.languageSelectLabel,
            isRequired: true,
            options: [],
            selectValue: { key: '', value: '' },
            customClass: 'col-12 col-md-6'
        },
        {
            id: InputID.JobTitle,
            type: InputType.Text,
            label: this.props.resources.jobTitleLabel,
            value: '',
            isRequired: false,
            customClass: 'col-12 col-md-6'
        },
        // Empty space for styling
        {
            id: InputID.Empty,
            type: InputType.Empty,
            label: '',
            value: '',
            isRequired: false,
            customClass: 'col-12 col-md-6'
        },

        // Company Data
        {
            id: InputID.CompanySectionHeader,
            type: InputType.SectionHeader,
            label: this.props.resources.companySectionHeaderLabel,
            value: this.props.resources.companySectionHeader,
            isRequired: false,
            customClass: 'col-12 col-md-6',
            number: '3'
        },

        {
            id: InputID.CompanyName,
            type: InputType.Text,
            label: this.props.resources.companyLabel,
            value: '',
            isRequired: true,
            customClass: 'col-12',
            maxChars: '60'
        },
        {
            id: InputID.Street,
            type: InputType.Text,
            label: this.props.resources.streetLabel,
            value: '',
            isRequired: true,
            customClass: 'col-9 col-md-9'
        },
        {
            id: InputID.HouseNumber,
            type: InputType.Text,
            label: this.props.resources.numberLabel,
            value: '',
            isRequired: true,
            customClass: 'col-3 col-md-3'
        },
        {
            id: InputID.ZipCode,
            type: InputType.Text,
            label: this.props.resources.zipLabel,
            value: '',
            isRequired: true,
            customClass: 'col-3 col-md-2'
        },
        {
            id: InputID.City,
            type: InputType.Text,
            label: this.props.resources.cityLabel,
            value: '',
            isRequired: true,
            customClass: 'col-9 col-md-4'
        },
        {
            id: InputID.Country,
            type: InputType.Country,
            value: '',
            label: this.props.resources.countrySelect,
            isRequired: true,
            options: [],
            selectValue: { key: '', value: '' },
            customClass: 'col-12 col-md-6'
        },
        {
            id: InputID.AdditionalAddressInformation,
            type: InputType.Text,
            label: this.props.resources.additionalAddressLabel,
            value: '',
            isRequired: false,
            customClass: 'col-12 col-md-12'
        },
        {
            id: InputID.CompanyType,
            type: InputType.CompanyType,
            value: '',
            label: this.props.resources.companyTypeLabel,
            isRequired: true,
            options: [],
            selectValue: { key: '', value: '' },
            customClass: 'col-12 col-md-6'
        },
        {
            id: InputID.Size,
            type: InputType.Select,
            label: this.props.resources.companySizeLabel,
            options: companySizes,
            value: '',
            selectValue: { key: '', value: '' },
            isRequired: false,
            customClass: 'col-12 col-md-6'
        },
        {
            id: InputID.ExistingCustomerNumber,
            type: InputType.Text,
            label: this.props.resources.exisitingCustomerNumberLabel,
            value: '',
            isRequired: false,
            customClass: 'col-6'
        },
        {
            id: InputID.OriginOfInterest,
            type: InputType.OriginOfInterest,
            label: this.props.resources.originOfInterestLabel,
            value: '',
            options: [],
            selectValue: { key: '', value: '' },
            isRequired: false,
            customClass: 'col-6'
        },
        {
            id: InputID.VATIdSectionHeader,
            type: InputType.SectionHeader,
            label: '',
            value: this.props.resources.vatIdSectionHeader,
            isRequired: false,
            customClass: 'col-12',
            number: '4'
        },
        {
            id: InputID.VAT,
            type: InputType.VAT,
            label: '',
            value: '',
            isRequired: false,
            isValid: false,
            customClass: 'col-12'
        }
    ];

    private readonly formChildReference: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    private readonly moduleClassName: string = 'nos-business-sign-up';

    private needsToReset: boolean = false;

    private readonly telemetryContent: ITelemetryContent;

    constructor(props: IBusinessSignUpExtendedProps) {
        super(props);

        this.state = {
            isContinueWithoutVatId: false,
            isVatValidationClicked: false,
            isEmailVerificationInProcess: false,
            isEmailVerified: false,
            isUserVerificationFailed: false,
            isReady: false,
            isVatCheckExcecuted: false,
            isVatCheckFirstClickDone: false,
            isAcceptedTermsAndConditions: false,
            formItems: this.props.formItems || this._addedReference(),
            addressHasErrors: false,
            showAddressErrors: false,
            hasSubmitErrors: false,
            isSubmittedPressed: false,
            emailValidationCode: '',
            isUserExisting: false,
            hasExistingProspect: false,
            isVatIdValid: false,
            suggestions: [],
            selectedSuggestion: '',
            originOfInterestReferral: ''
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public componentDidMount(): void {
        this.formChildReference.current?.focus();
    }

    public shouldComponentUpdate(nextProps: IBusinessSignUpExtendedProps, nextState: IBusinessState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const { config } = this.props;
        const { formItems } = this.state;
        const { className, heading } = config;

        const viewProps = {
            ...this.props,
            businessSignUp: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, className)
            },
            ContainerWrapper: {
                className: `${this.moduleClassName}__container`
            },
            heading: (
                <Text
                    className={`${this.moduleClassName}__page-heading`}
                    {...heading}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            businessSignUpForm: this._renderForm(),
            formItems
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handle the heading change event.
     * @param event - Content Editable Event.
     */
    public handleHeadingChange = (event: ContentEditableEvent): void => {
        this.props.config.heading.text = event.target.value;
    };

    private _renderForm(): IBusinessSignUpLocalAccount {
        const { config, resources, slots } = this.props;
        const {
            showAddressErrors,
            hasSubmitErrors,
            isReady,
            formItems,
            isContinueWithoutVatId,
            isEmailVerificationInProcess,
            isEmailVerified,
            isUserVerificationFailed,
            isVatValidationClicked,
            isVatCheckFirstClickDone,
            isVatCheckExcecuted,
            emailValidationCode,
            isSubmittedPressed,
            isUserExisting,
            hasExistingProspect,
            suggestions,
            selectedSuggestion
        } = this.state;
        const {
            signUpButtonArialabel,
            signUpButtonText,
            cancelButtonArialabel,
            cancelButtonText,
            errorMessage,
            partialAddressErrorMessage
        } = resources;
        const languageSlot = (slots && slots.languageSlot && slots.languageSlot.length > 0 && slots.languageSlot[0]) || undefined;
        const countrySelect = (slots && slots.countrySlot && slots.countrySlot.length > 0 && slots.countrySlot[0]) || undefined;
        const companyTypeSelect =
            (slots && slots.companyTypeSlot && slots.companyTypeSlot.length > 0 && slots.companyTypeSlot[0]) || undefined;
        const phoneSlot = (slots && slots.phoneSlot && slots.phoneSlot.length > 0 && slots.phoneSlot[0]) || undefined;
        const originOfInterest =
            (slots && slots.originOfInterestSlot && slots.originOfInterestSlot.length > 0 && slots.originOfInterestSlot[0]) || undefined;

        const successPageURL =
            (config.confirmationLink && config.confirmationLink.linkUrl && config.confirmationLink.linkUrl.destinationUrl) ||
            getUrlSync('home', this.props.context.actionContext);

        const url = this.props.context.request.user.signInUrl || '';

        const handleSuggestionSelect = (suggestionId: string) => {
            const foundSuggestion: Suggestion = suggestions.find((suggestion: Suggestion) => suggestion.id === suggestionId) || {
                id: ''
            };
            if (foundSuggestion.address?.country) {
                this._updateCountry({
                    key: foundSuggestion.address.countryCodeISO3 || '',
                    value: foundSuggestion.address.country
                });
            }

            this.setState({ selectedSuggestion: suggestionId });
        };

        const form = {
            FormWrapper: {
                className: 'row'
            },
            items: renderInputSection(
                url,
                resources,
                formItems,
                `${this.moduleClassName}__form-item`,
                this.needsToReset,
                this._onInputChange,
                showAddressErrors,
                this.telemetryContent,
                emailValidationCode,
                isSubmittedPressed,
                isUserExisting,
                hasExistingProspect,
                languageSlot,
                countrySelect,
                companyTypeSelect,
                originOfInterest,
                phoneSlot,
                this._onAddressChange,
                this._onButtonClick,
                this.moduleClassName,
                isContinueWithoutVatId,
                isVatValidationClicked,
                isVatCheckFirstClickDone,
                this._onIsContinueWithoutVatIdChange,
                isEmailVerificationInProcess,
                isEmailVerified,
                isUserVerificationFailed,
                this._confirmEmailCode,
                this._updateLanguage,
                this._updateCountry,
                this._updateCompanyType,
                this._updateOriginOfInterest,
                this._updatePhone,
                this._updatePhoneCode,
                isVatCheckExcecuted,
                suggestions,
                selectedSuggestion,
                handleSuggestionSelect,
                this._updateCompanyAddress
            ),
            buttons: [
                <SignUpButton
                    key='continue'
                    id='continue'
                    className={`ms-button__primary-dark my-m ${!isReady && 'button-fake-disabled'}`}
                    link={successPageURL}
                    ariaLabel={signUpButtonArialabel}
                    text={signUpButtonText}
                    disabled={!isReady}
                    onClick={this._onSubmit}
                    telemetryContent={this.telemetryContent}
                />,
                <SignUpButton
                    key='cancel'
                    id='cancel'
                    className={`${this.moduleClassName}__cancel-button`}
                    ariaLabel={cancelButtonArialabel}
                    text={cancelButtonText}
                    onClick={this._onCancel}
                    telemetryContent={this.telemetryContent}
                />
            ],
            errorMessage: [
                hasSubmitErrors && <SignUpError key='error' id='error' className={this.moduleClassName} message={errorMessage} />,
                showAddressErrors && (
                    <SignUpError key='error' id='error' className={this.moduleClassName} message={partialAddressErrorMessage} />
                )
            ],
            disclaimer: [
                <SignUpTermsConditions
                    key='terms-and-conditions'
                    resources={resources}
                    id='termsAndConditions'
                    type='checkbox'
                    name='termsAndConditions'
                    className={this.moduleClassName}
                    isAcceptedTermsAndConditions={this.state.isAcceptedTermsAndConditions}
                    onChange={this._onIsAcceptedTermsAndConditions}
                    isSubmittedPressed={isSubmittedPressed}
                />
            ]
        };

        if (this.needsToReset) {
            this.needsToReset = false;
        }

        return form;
    }

    private debouncedHandleSearch = debounce(async (query: string, type: string, limit: number) => {
        if (query.length >= NOS_BUSINESS_SIGN_UP_AUTOCOMPLETE_CHAR_LIMIT) {
            try {
                const actionInput = new GetAddressSuggestionInput(type, query, limit);
                const response = await commerceApiRequest(this.props.context.request, 'GetAddressSuggestion', 'post', actionInput);
                const responseData = toJS(response).data as IGetAddressSuggestionData;
                const addressSuggestions = responseData.results;
                if (addressSuggestions.length > 0) {
                    this.setState({ suggestions: addressSuggestions });
                } else {
                    this.setState({ suggestions: [] });
                }
            } catch (error) {
                console.error('Error fetching search results:', error);
            }
        }
    }, NOS_BUSINESS_SIGN_UP_AUTOCOMPLETE_DEBOUNCE_TIMEOUT);

    private readonly _onInputChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLButtonElement | HTMLSelectElement>
    ): void => {
        const AutocompleteSearch = (query: string, type: string, limit: number) => {
            if (!limit) {
                limit = 8;
            }
            void this.debouncedHandleSearch(query, type, limit);
        };

        const newForm = this.state.formItems;

        let isEmailVerified;

        newForm.forEach(item => {
            if (item?.id === event.target?.id) {
                item.value = event.target.value;

                AutocompleteSearch(item.value, item.id, this.props.config.azureMapsSuggestionLimit);

                if (item.type === InputType.Select) {
                    item.selectValue = item.options?.find(el => el.value === event.target.value);
                    if (item.selectValue?.key) {
                        item.value = item.selectValue.value;
                    }
                }

                // A change in the VAT makes it invalid so the user has to validate it again.
                if (item.id === InputID.VAT) {
                    this.setState({ isVatValidationClicked: false, isVatCheckExcecuted: false });
                    item.isValid = false;
                }

                // Makes Email invalid again.
                if (item?.id === InputID.EmailVerification) {
                    this.setState({ isEmailVerificationInProcess: false });
                    this.setState({ isEmailVerified: false });
                    this.setState({ isUserVerificationFailed: false });
                    isEmailVerified = false;
                }
            }
        });

        if (event.target?.id === InputID.EmailValidation) {
            this.setState({ emailValidationCode: event.target.value });
        }

        // Check if form is ready, update state object with latest values, values have changed so reset hasSubmitErrros

        this.setState({
            isReady:
                event.target.id === InputID.VAT
                    ? false
                    : this._isFormReady(isEmailVerified, this.state.isContinueWithoutVatId, this.state.isVatValidationClicked),
            formItems: newForm,
            hasSubmitErrors: false
        });
    };

    private _addedReference(): IFormItem[] {
        const newFormFields = this.BusinessSignUpDefaultInputs;
        newFormFields[0].fieldReference = this.formChildReference;
        return newFormFields;
    }

    private _updateCompanyAddress = (inputType: string, newValue: string) => {
        const newForm = [...this.state.formItems];
        const formItem = newForm.find(item => item.id === inputType);

        if (formItem) {
            formItem.value = newValue;

            this.setState({
                isReady: this._isFormReady(
                    this.state.isEmailVerified,
                    this.state.isContinueWithoutVatId,
                    this.state.isVatValidationClicked
                ),
                formItems: newForm
            });
        }
    };

    private readonly _onAddressChange = (errors: boolean, currentAddress?: Address): void => {
        // Find address item
        const newForm = this.state.formItems;
        const addressItem = newForm.find(item => {
            return item.type === InputType.Address;
        });

        // If found --> set to currentAddress
        if (addressItem) {
            addressItem.address = currentAddress;

            // Update state with new values and set errors to false if address is not required field
            this.setState({
                isReady: this._isFormReady(
                    this.state.isEmailVerified,
                    this.state.isContinueWithoutVatId,
                    this.state.isVatValidationClicked
                ),
                formItems: newForm,
                addressHasErrors: errors,
                showAddressErrors: false
            });
        }
    };

    private readonly _onIsContinueWithoutVatIdChange = () => {
        this.setState({
            isReady: this._isFormReady(this.state.isEmailVerified, !this.state.isContinueWithoutVatId, this.state.isVatValidationClicked),
            isContinueWithoutVatId: !this.state.isContinueWithoutVatId
        });
    };

    private readonly _onIsAcceptedTermsAndConditions = () => {
        this.setState(
            {
                isAcceptedTermsAndConditions: !this.state.isAcceptedTermsAndConditions
            },
            () => {
                this._changeIsAcceptedTermsAndConditions();
            }
        );
    };

    private readonly _changeIsAcceptedTermsAndConditions = () => {
        this.setState({
            isReady: this._isFormReady(this.state.isEmailVerified, this.state.isContinueWithoutVatId, this.state.isVatValidationClicked)
        });
    };

    private readonly _onButtonClick = (id: string) => {
        if (id === InputType.EmailVerification) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._onVerifyEmailClick();
        } else {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._onValidateVATClick();
        }
    };

    private readonly _onValidateVATClick = async () => {
        const vatId = this._getItem(InputID.VAT);
        const companyName = this._getItem(InputID.CompanyName);
        if (vatId) {
            const newFormItems = this.state.formItems;
            const actionInput = new ValidateVatInput(vatId.value, companyName?.value ?? '');
            await validateVatAction(actionInput, this.props.context.actionContext).then(res => {
                newFormItems.forEach(el => {
                    if (el.id === InputID.VAT) {
                        el.isValid = res.response.isValid;
                    }
                });
                this.setState({
                    formItems: newFormItems,
                    isVatCheckExcecuted: res.response.isVatCheckExcecuted ?? false,
                    isVatIdValid: res.response.isValid
                });
            });
        }

        this.setState({
            isReady: this._isFormReady(this.state.isEmailVerified, this.state.isContinueWithoutVatId, true),
            isVatValidationClicked: true,
            isVatCheckFirstClickDone: true
        });
    };

    private readonly _onVerifyEmailClick = async () => {
        const emailAddress = this._getItem(InputID.EmailVerification);
        if (emailAddress) {
            const actionInput = new VerifyEmailInput(emailAddress.value);
            await verifyEmailAction(actionInput, this.props.context.actionContext).then(res => {
                this.setState({ isEmailVerificationInProcess: res.response, isUserVerificationFailed: false });
            });
        }
    };

    private readonly _confirmEmailCode = async (code: string) => {
        const emailAddress = this._getItem(InputID.EmailVerification);
        if (emailAddress) {
            const actionInput = new ValidateEmailCodeInput(emailAddress.value, code);
            await validateEmailCodeAction(actionInput, this.props.context.actionContext).then((res: any) => {
                if (!res.validCode) {
                    this.setState({ isUserVerificationFailed: true });
                } else if (res.validCode && res.isUserExisting) {
                    this.setState({
                        isUserVerificationFailed: true,
                        isUserExisting: true
                    });
                } else if (res.validCode && !res.isUserExisting) {
                    this.setState({
                        isUserVerificationFailed: res.existingProspect,
                        isUserExisting: false,
                        isEmailVerified: !res.existingProspect,
                        hasExistingProspect: res.existingProspect
                    });
                }
                this.setState({
                    isReady: this._isFormReady(
                        !this.state.isUserVerificationFailed,
                        this.state.isContinueWithoutVatId,
                        this.state.isVatValidationClicked
                    )
                });
            });
        }
    };

    private readonly _updateLanguage = (newChosenLanguage: IGetAvailableLanguagesData) => {
        // Find address item
        const newForm = this.state.formItems;
        const languageItem = newForm.find(item => {
            return item.type === InputType.Language;
        });

        // If found --> set to currentAddress
        if (languageItem) {
            languageItem.value = newChosenLanguage.value;
            languageItem.selectValue = newChosenLanguage;

            // Update state with new values and set errors to false if address is not required field
            this.setState({
                isReady: this._isFormReady(
                    this.state.isEmailVerified,
                    this.state.isContinueWithoutVatId,
                    this.state.isVatValidationClicked
                ),
                formItems: newForm
            });
        }
    };

    private readonly _updateCountry = (newChosenCountry: IGetAvailableLanguagesData) => {
        // Find address item
        const newForm = this.state.formItems;
        const countryItem = newForm.find(item => {
            return item.type === InputType.Country;
        });

        // If found --> set to currentAddress
        if (countryItem) {
            countryItem.value = newChosenCountry.value;
            countryItem.selectValue = newChosenCountry;

            // Update state with new values and set errors to false if address is not required field
            this.setState({
                isReady: this._isFormReady(
                    this.state.isEmailVerified,
                    this.state.isContinueWithoutVatId,
                    this.state.isVatValidationClicked
                ),
                formItems: newForm
            });
        }
    };
    private readonly _updateCompanyType = (newChosenCompanyType: IGetAvailableLanguagesData) => {
        // Find address item
        const newForm = this.state.formItems;
        const companyTypeItem = newForm.find(item => {
            return item.type === InputType.CompanyType;
        });

        // If found --> set to currentAddress
        if (companyTypeItem) {
            companyTypeItem.value = newChosenCompanyType.value;
            companyTypeItem.selectValue = newChosenCompanyType;

            // Update state with new values and set errors to false if address is not required field
            this.setState({
                isReady: this._isFormReady(
                    this.state.isEmailVerified,
                    this.state.isContinueWithoutVatId,
                    this.state.isVatValidationClicked
                ),
                formItems: newForm
            });
        }
    };

    private readonly _updateOriginOfInterest = (newChosenOriginOfInterest: { key: string; value: string }) => {
        this.setState(prev => {
            const newForm = prev.formItems.map(item => {
                if (item.id === InputID.OriginOfInterest) {
                    return {
                        ...item,
                        value: newChosenOriginOfInterest.value,
                        selectValue: newChosenOriginOfInterest
                    };
                }
                return item;
            });

            return {
                formItems: newForm,
                isReady: this._isFormReady(prev.isEmailVerified, prev.isContinueWithoutVatId, prev.isVatValidationClicked)
            };
        });
    };

    private readonly _updatePhone = (newInput: string) => {
        const newForm = this.state.formItems;
        const phoneItem = newForm.find(item => {
            return item.type === InputType.Phone;
        });
        if (phoneItem) {
            phoneItem.value = newInput;
            this.setState({
                isReady: this._isFormReady(
                    this.state.isEmailVerified,
                    this.state.isContinueWithoutVatId,
                    this.state.isVatValidationClicked
                ),
                formItems: newForm
            });
        }
    };
    private readonly _updatePhoneCode = (newChosenPhoneCode: string) => {
        const newForm = this.state.formItems;
        const phoneCodeItem = newForm.find(item => {
            return item.type === InputType.Phone;
        });

        if (phoneCodeItem) {
            phoneCodeItem.selectValue = newChosenPhoneCode;
            this.setState({
                isReady: this._isFormReady(
                    this.state.isEmailVerified,
                    this.state.isContinueWithoutVatId,
                    this.state.isVatValidationClicked
                ),
                formItems: newForm
            });
        }
    };

    private readonly _isFormReady = (
        isEmailVerified?: boolean,
        isContinueWithoutVatId?: boolean,
        isVatValidationClicked?: boolean
    ): boolean => {
        const { formItems } = this.state;

        for (const currentInput of formItems) {
            // Will return FALSE if:
            //  - input field is required
            //      IF is address AND addressHasErrors === true
            //      ELSE value is undefined OR empty
            //  OR
            //  - pattern provided and value does not match pattern
            // OR
            // - accepts terms and conditions checkbox is not checked

            if (currentInput.isRequired) {
                if (currentInput.value === undefined || currentInput.value === '') {
                    return false;
                }
                if (currentInput.id === InputID.Phone) {
                    if (currentInput.value === '' || currentInput.selectValue === '+' || currentInput.selectValue === undefined) {
                        return false;
                    }
                }
            }
            if (currentInput.id === InputID.VAT) {
                if (!(!currentInput.isValid && isVatValidationClicked && !this.state.isVatCheckExcecuted)) {
                    if (!currentInput.isValid) {
                        if (!isContinueWithoutVatId) {
                            return false;
                        }
                    }
                }
            }

            if (currentInput.pattern !== undefined && currentInput.value.match(currentInput.pattern) === null) {
                return false;
            }
        }
        if (isEmailVerified !== undefined && isEmailVerified === false) {
            return false;
        } else if (!this.state.isEmailVerified) {
            return false;
        }
        if (!this.state.isAcceptedTermsAndConditions) {
            return false;
        }
        return true;
    };

    private readonly _onSubmit = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        this.setState({ isSubmittedPressed: true });

        // // Check for campaignCode / voucherCode in url
        const currentUrl = new URL(window.location.href);
        const campaignCode = decodeURIComponent(currentUrl.searchParams.get(NOS_BUSINESS_SIGN_UP_CAMPAIGN_CODE_PARAM) || '');

        if (this.state.isReady) {
            if (this.state.addressHasErrors) {
                this.setState({
                    showAddressErrors: true
                });

                event.preventDefault();
                return;
            }

            const { context } = this.props;

            // Check for matching id and if not empty
            const firstName = this._getItem(InputID.FirstName);
            const lastName = this._getItem(InputID.LastName);
            const companyName = this._getItem(InputID.CompanyName);
            const companyEmail = this._getItem(InputID.EmailVerification);
            const jobTitle = this._getItem(InputID.JobTitle);
            const companySize = this._getItem(InputID.Size);
            const comments = this._getItem(InputID.Comment);
            const companyCity = this._getItem(InputID.City);
            const companyStreet = this._getItem(InputID.Street);
            const companyHouseNumber = this._getItem(InputID.HouseNumber);
            const companyZipCode = this._getItem(InputID.ZipCode);
            const companyPhone = this._getItem(InputID.Phone);
            const country = this._getItem(InputID.Country);
            const existingCustomerNumber = this._getItem(InputID.ExistingCustomerNumber);
            const language = this._getItem(InputID.Language);
            const companyType = this._getItem(InputID.CompanyType);
            const originOfInterest = this._getItem(InputID.OriginOfInterest);
            const additionalAddressInformation = this._getItem(InputID.AdditionalAddressInformation);
            const vatId = this._getItem(InputID.VAT);
            const isVatIdValid = this.state.isVatIdValid;
            const isVatCheckExcecuted = this.state.isVatCheckExcecuted;

            const newCompanyAddress: Address = {
                Email: companyEmail?.value,
                City: companyCity?.value,
                Street: `${companyStreet?.value} ${companyHouseNumber?.value}`,
                StreetNumber: companyHouseNumber?.value,
                ZipCode: companyZipCode?.value,
                Phone: `${companyPhone?.selectValue}${companyPhone?.value}`, // Once it has to be with dropdown, choose below line
                ThreeLetterISORegionName: typeof country?.selectValue === 'object' ? country?.selectValue?.key : '',
                AddressTypeValue: 9
            };

            const ExtensionProperties: CommerceProperty[] = [
                {
                    Key: 'TECExistingCustomerNumber',
                    Value: {
                        StringValue: existingCustomerNumber?.value
                    }
                },
                {
                    Key: 'TECLanguage',
                    Value: {
                        StringValue: typeof language?.selectValue === 'object' ? language?.selectValue?.key : ''
                    }
                },
                {
                    Key: 'TECCompanyTypeCode',
                    Value: {
                        StringValue: typeof companyType?.selectValue === 'object' ? companyType?.selectValue?.key : ''
                    }
                },
                {
                    Key: 'TECAdditionalInfo',
                    Value: {
                        StringValue: additionalAddressInformation?.value
                    }
                },
                {
                    Key: 'TECSourceTypeId',
                    Value: {
                        StringValue: typeof originOfInterest?.selectValue === 'object' ? originOfInterest?.selectValue?.key : ''
                    }
                },
                {
                    Key: 'TECVatId',
                    Value: {
                        StringValue: vatId?.value
                    }
                },
                {
                    Key: 'TECVatIdIsValid',
                    Value: {
                        BooleanValue: isVatIdValid
                    }
                },
                {
                    Key: 'TECIsVatCheckExcecuted',
                    Value: {
                        BooleanValue: isVatCheckExcecuted
                    }
                },
                {
                    Key: 'TECCampaignCode',
                    Value: {
                        StringValue: campaignCode || ''
                    }
                }
            ];

            const newPartner: BusinessPartnerProspect = {
                // TODO: Include Vat and change BusinessPartnerProspect
                FirstName: firstName && firstName.value,
                LastName: lastName && lastName.value,
                CompanyName: companyName && companyName.value,
                Address: newCompanyAddress,
                JobTitle: jobTitle && jobTitle.value,
                CompanySize: (companySize && Number.parseInt(companySize.value, 10)) || 0,
                Comments: comments && comments.value,
                ExtensionProperties: ExtensionProperties
            };

            // Prevent the a href in order to execute the async request first (firefox specific workaround)
            event.preventDefault();

            // Create business partner request
            createProspectAsync({ callerContext: context.actionContext }, newPartner)
                .catch(() => {
                    this.setState({
                        hasSubmitErrors: true,
                        isReady: false
                    });
                })
                .finally(() => {
                    const config = this.props.config;
                    window.location.href =
                        (config.confirmationLink && config.confirmationLink.linkUrl && config.confirmationLink.linkUrl.destinationUrl) ||
                        getUrlSync('home', this.props.context.actionContext) ||
                        '';
                });
        }
    };

    private readonly _onCancel = () => {
        // Reset values of the form
        const cleanForm = this.state.formItems;
        cleanForm.forEach(input => {
            input.value = '';
        });

        // Set reset flag so address input will get reset on next renderInputSection() call
        this.needsToReset = true;

        this.setState({
            isReady: false,
            formItems: cleanForm,
            addressHasErrors: true,
            showAddressErrors: false,
            hasSubmitErrors: false
        });
    };

    private readonly _getItem = (id: string, isAddress: boolean = false) => {
        if (isAddress) {
            return this.state.formItems.find(item => {
                return item.id === id && item.address;
            });
        }
        return this.state.formItems.find(item => {
            if (item?.id === id) {
                if (id === InputID.Phone) {
                    return item;
                }
                return item.value !== '';
            } else {
                return undefined;
            }
        });
    };
}

export default NosBusinessSignUp;
